/**
 * ShowMenu: which tests and tasks the Data refinery tree shows, one view
 * at a time (tree.ts `TreeFilter`). Each option says how many tests or
 * tasks it keeps, so staff see the size of a view before they pick it.
 */

import { useId } from 'react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  REFINERY_COPY,
  allTestsOption,
  alwaysPassedOption,
  policyFailsOption,
  verdictOption,
} from './rows'
import { type FilterCounts, type TreeFilter, isTreeFilter } from './tree'

interface ShowMenuProps {
  value: TreeFilter
  onChange: (filter: TreeFilter) => void
  /** Every test of the tree. */
  tests: number
  counts: FilterCounts
}

export function ShowMenu({ value, onChange, tests, counts }: ShowMenuProps): React.JSX.Element {
  const id = useId()
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="text-xs">
        {REFINERY_COPY.show.label}
      </Label>
      <Select
        value={value}
        onValueChange={(next) => {
          if (isTreeFilter(next)) onChange(next)
        }}
      >
        <SelectTrigger id={id} className="h-8 w-56 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{allTestsOption(tests)}</SelectItem>
          <SelectGroup>
            <SelectLabel>{REFINERY_COPY.show.verdict}</SelectLabel>
            {counts.verdicts.map(({ verdict, tests: count }) => (
              <SelectItem key={verdict} value={verdict}>
                {verdictOption(verdict, count)}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>{REFINERY_COPY.show.basePolicy}</SelectLabel>
            <SelectItem value="policyFails">{policyFailsOption(counts.policyFails)}</SelectItem>
            <SelectItem value="alwaysPassed">{alwaysPassedOption(counts.alwaysPassed)}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}
